import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import AuthModal from '../Auth/AuthModal';
import ThemeToggle from '../UI/ThemeToggle';
import api from '../../services/api';

const Header = () => {
  const { isAuthenticated, user } = useAuth();
  const { isDark } = useTheme();
  const location = useLocation();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalView, setAuthModalView] = useState('login');
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isNavigating, setIsNavigating] = useState(false);
  const navigationTimeoutRef = useRef(null);

  const handleLoginClick = () => {
    setAuthModalView('login');
    setShowAuthModal(true);
  };

  const handleRegisterClick = () => {
    setAuthModalView('register');
    setShowAuthModal(true);
  };

  const endImpersonation = async () => {
    // Restore original token
    const originalToken = localStorage.getItem('originalToken');
    if (originalToken) {
      localStorage.setItem('accessToken', originalToken);
      localStorage.removeItem('originalToken');
      localStorage.removeItem('impersonationToken');
      
      // Fetch the original admin user data
      try {
        const response = await api.get('/auth/me');
        localStorage.setItem('user', JSON.stringify(response.data.user));
      } catch (error) {
        console.error('Failed to fetch admin user data:', error);
      }
      
      window.location.href = '/admin';
    }
  };

  // Custom debounce implementation
  function debounce(func, wait) {
    let timeout;
    const debounced = function(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
    debounced.cancel = () => clearTimeout(timeout);
    return debounced;
  }

  // Detect navigation
  useEffect(() => {
    setIsNavigating(true);
    
    // Clear any existing timeout
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }
    
    // Wait for navigation to complete
    navigationTimeoutRef.current = setTimeout(() => {
      setIsNavigating(false);
    }, 300);
    
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, [location.pathname]);

  // Reset header visibility on route change
  useEffect(() => {
    setIsVisible(true);
    setLastScrollY(0);
  }, [location.pathname]);

  // Handle scroll to hide/show header
  useEffect(() => {
    const handleScroll = debounce(() => {
      // Skip if navigating
      if (isNavigating) return;
      
      const currentScrollY = window.scrollY;
      const scrollDelta = currentScrollY - lastScrollY;
      
      // Only hide/show for significant scroll
      if (Math.abs(scrollDelta) < 5) return;
      
      // Hide header when scrolling down past threshold
      if (scrollDelta > 0 && currentScrollY > 80) {
        console.log('Hiding header - scrollY:', currentScrollY);
        setIsVisible(false);
      } 
      // Show header when scrolling up
      else if (scrollDelta < 0) {
        console.log('Showing header - scrollY:', currentScrollY);
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    }, 50); // 50ms debounce

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      handleScroll.cancel(); // Cancel debounced function
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY, isNavigating]);

  return (
    <>
      <header 
        className={`header-glass fixed left-0 right-0 z-50 transition-all duration-300 ${
          isVisible ? 'top-0 translate-y-0 opacity-100' : '-top-32 -translate-y-full opacity-0 pointer-events-none'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src={isDark ? "/white-logo.png" : "/eyehill-logo.png"}
                alt="Eyehill AEO" 
                className="h-16 w-auto"
              />
            </div>

            {/* Navigation Controls */}
            <div className="flex items-center space-x-4">
              {/* Impersonation Indicator */}
              {user?.isImpersonation && (
                <div className="flex items-center space-x-3 mr-4">
                  <div className="glass-alert-warning px-3 py-1 rounded-full flex items-center space-x-2">
                    <span className="text-mono text-xs">Viewing as: {user.email}</span>
                    <button
                      onClick={endImpersonation}
                      className="text-xs font-medium text-yellow-600 hover:text-yellow-700 underline"
                    >
                      End
                    </button>
                  </div>
                </div>
              )}
              
              {/* Theme Toggle */}
              <ThemeToggle />
              
              {/* Authentication Controls - Only show for non-authenticated users */}
              {!isAuthenticated && (
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleLoginClick}
                    className="btn-glass inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg font-sans focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={handleRegisterClick}
                    className="btn-glass-accent inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg font-sans focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
                  >
                    Sign Up
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Authentication Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialView={authModalView}
      />
    </>
  );
};

export default Header;